/**
 * ONE query factory per detail entity: the single source of truth for each
 * detail route's queryKey + queryFn (the purchase route's `purchaseStatusQuery`
 * established the pattern, perf audit part 2, Theme 1). The route loaders seed
 * through these factories and the detail pages' `useQuery` calls consume the
 * same key, so the loader-seeded cache entry and the mounted query can never
 * drift in shape — and the data-prefetch primitives
 * (`@/lib/prefetch/detail-data`) can warm the exact entry a tap will read.
 *
 * Keys here are byte-identical to the ones the loaders (and their
 * loader-contract tests) pinned before this module existed; the refactor is a
 * pure key-source move, not a key change.
 */
import { authenticatedGetShared } from '@/lib/api/authenticated-shared';
import type { DetailQuery } from '@/lib/prefetch/detail-data';

export type { DetailQuery };
export type PlainDetailQuery = { queryKey: readonly unknown[]; queryFn: () => Promise<unknown> };
export type InfiniteDetailQuery = {
  queryKey: readonly unknown[];
  queryFn: (context: { pageParam: unknown }) => Promise<unknown>;
  infinite: true;
  initialPageParam: unknown;
};
import { normalizeListResponse, type FeatureRequestsResponse } from '@/lib/api/v2/feature-requests';
import type { V2FavoritesResponse } from '@/lib/api/v2/favorites';
import type { InstructorReviewsResponseV2, MyStudioReviewsResponseV2 } from '@/types/discovery';
import { getClassDetail } from '@/lib/api/v2/classes';
import { getInstructor } from '@/lib/api/v2/instructor';
import { marketApi } from '@/lib/api/v2/market';
import { getStudio } from '@/lib/api/v2/studio';
import type { FeatureRequestDetailV2 } from '@/lib/api/v2/feature-requests';
import type { PaginatedPostsV2, PostDetailV2 } from '@/lib/api/v2/community';
import type { PrivateProfile } from '@/lib/api/v2/profile';
import { getClasses } from '@/lib/api/v2/discovery';
import { discoveryClassesKey } from '@/hooks/data/useDiscovery';
import { getHomeFeatureRequests, getHomeUpcomingClasses } from '@/lib/server-fns/homepage-fns';
import { toSGTCivilKey } from '@/lib/utils/date';

/** What the ['class', id] cache entry holds: getClassDetail's raw Result. */
export type ClassQueryData = Awaited<ReturnType<typeof getClassDetail>>;

/** What the ['instructor', id] cache entry holds: getInstructor's raw Result. */
export type InstructorQueryData = Awaited<ReturnType<typeof getInstructor>>;

/** What the ['studio', id] cache entry holds: getStudio's raw Result. */
export type StudioQueryData = Awaited<ReturnType<typeof getStudio>>;

const fetchMarketProduct = async (productId: string) => {
  const r = await marketApi.getProduct(productId);
  if (!r.ok) throw new Error(r.error.message);
  return r.value;
};

/**
 * What the ['market', 'products', id] cache entry holds: the UNWRAPPED product
 * (same entry `useMarketProduct` reads — the queryFn throws on API errors so
 * ensureQueryData rejects into the loader's ok:false envelope path).
 */
export type MarketProductQueryData = Awaited<ReturnType<typeof fetchMarketProduct>>;

export const classDetailQuery = (classId: string) => ({
  queryKey: ['class', classId] as const,
  queryFn: () => getClassDetail(classId),
});

export const instructorDetailQuery = (instructorId: string) => ({
  queryKey: ['instructor', instructorId] as const,
  queryFn: () => getInstructor(instructorId),
});

export const studioDetailQuery = (studioId: string) => ({
  queryKey: ['studio', studioId] as const,
  queryFn: () => getStudio(studioId),
});

export const marketProductQuery = (productId: string) => ({
  queryKey: ['market', 'products', productId] as const,
  queryFn: () => fetchMarketProduct(productId),
});

/**
 * Same key + shape the community detail page's useQuery consumes
 * (post-detail-page.tsx). authenticatedGetShared picks the cookie-forwarding
 * server fn on SSR and the direct Go API call on client navigations (perf
 * audit part 2, Theme 1).
 */
export const communityPostQuery = (postId: string) => ({
  queryKey: ['community-post', postId] as const,
  queryFn: () => authenticatedGetShared<PostDetailV2>(`/v2/community/posts/${postId}`),
});

/**
 * Dual transport (#1290 pattern): authenticatedGetShared forwards cookies
 * through the server fn during SSR and calls the Go API directly on client
 * navigations — same endpoint and unwrapped shape the detail page's useQuery
 * (feature-request-detail-page.tsx) reads.
 */
export const featureRequestQuery = (featureRequestId: string) => ({
  queryKey: ['feature-request', featureRequestId] as const,
  queryFn: () =>
    authenticatedGetShared<FeatureRequestDetailV2>(`/v2/feature-requests/${featureRequestId}`),
});

// ---- List destinations (profile family + feature requests) ----------------
// These seed the same cache entries their route loaders gate on, so the
// entry cards can data-prefetch a whole list surface ahead of the tap.

export const featureRequestListQuery = (): InfiniteDetailQuery => ({
  queryKey: ['feature-requests', 'all', 'votes'] as const,
  queryFn: ({ pageParam }: { pageParam: unknown }) => {
    const qs = new URLSearchParams({ filter: 'all', sort: 'votes', limit: '20' });
    if (pageParam) qs.set('cursor', String(pageParam));
    return authenticatedGetShared<FeatureRequestsResponse>(
      `/v2/feature-requests?${qs.toString()}`
    ).then(normalizeListResponse);
  },
  infinite: true,
  initialPageParam: undefined as string | undefined,
});

export const favouritesQuery = (): PlainDetailQuery => ({
  queryKey: ['v2-favorites', 'instructors,studios'] as const,
  queryFn: () =>
    authenticatedGetShared<V2FavoritesResponse>(
      '/v2/me/favorites?types=instructors,studios&limit=100'
    ),
});

export const profileSocialQuery = (): InfiniteDetailQuery => ({
  queryKey: ['profile-posts', 'mine'] as const,
  queryFn: ({ pageParam }: { pageParam: unknown }) =>
    authenticatedGetShared(
      `/v2/community/posts?tab=all&filter=mine${pageParam ? `&cursor=${pageParam}` : ''}`
    ),
  infinite: true,
  initialPageParam: undefined as string | undefined,
});

export const myInstructorReviewsQuery = (): InfiniteDetailQuery => ({
  queryKey: ['v2-my-reviews', 'instructors'] as const,
  queryFn: ({ pageParam }: { pageParam: unknown }) =>
    authenticatedGetShared(
      `/v2/me/reviews?type=instructor&limit=50${pageParam ? `&cursor=${pageParam}` : ''}`
    ) as Promise<InstructorReviewsResponseV2>,
  infinite: true,
  initialPageParam: undefined as string | undefined,
});

export const myStudioReviewsQuery = (): InfiniteDetailQuery => ({
  queryKey: ['v2-my-reviews', 'studios'] as const,
  queryFn: ({ pageParam }: { pageParam: unknown }) =>
    authenticatedGetShared(
      `/v2/me/reviews?type=studio&limit=50${pageParam ? `&cursor=${pageParam}` : ''}`
    ) as Promise<MyStudioReviewsResponseV2>,
  infinite: true,
  initialPageParam: undefined as string | undefined,
});

// ---- Tab roots (bottom-navigation data prefetch) ---------------------------
// One factory per bottom-navigation destination's ROOT query, byte-identical to
// what its route loader seeds (profile/index.tsx, market/index.tsx,
// community/index.tsx, home/index.tsx, discovery.tsx default path). The loaders
// are cache-first gated on these exact keys, so an entry primed at touchstart
// resolves the committed navigation instantly — no post-commit API round trip.
// BottomNavigation wires these into `prefetchDetailData`, which TTL-throttles
// per key so repeated touches and hovers never refire the fetch.

/** The /profile hub's ['profile'] seed (profile/index.tsx loader). */
export const profileRootQuery = (): PlainDetailQuery => ({
  queryKey: ['profile'] as const,
  queryFn: () => authenticatedGetShared<PrivateProfile>('/v2/me/profile'),
});

/** The /market catalogue's non-blocking ['market', 'products'] prime
 *  (market/index.tsx loader) — list unwrap, NOT the single-product factory. */
export const marketProductsRootQuery = (): PlainDetailQuery => ({
  queryKey: ['market', 'products'] as const,
  queryFn: async () => {
    const r = await marketApi.listProducts();
    if (!r.ok) throw new Error(r.error.message);
    return r.value;
  },
});

/** The /community feed's ['community-posts', 'all'] infinite seed
 *  (community/index.tsx loader). */
export const communityPostsRootQuery = (): InfiniteDetailQuery => ({
  queryKey: ['community-posts', 'all'] as const,
  queryFn: ({ pageParam }: { pageParam: unknown }) => {
    const qs = new URLSearchParams({ tab: 'all' });
    if (pageParam) qs.set('cursor', String(pageParam));
    return authenticatedGetShared<PaginatedPostsV2>(`/v2/community/posts?${qs.toString()}`);
  },
  infinite: true,
  initialPageParam: undefined as string | undefined,
});

/** The two /home section seeds (home/index.tsx loader keys HOME_*). */
export const homeFeatureRequestsQuery = (): PlainDetailQuery => ({
  queryKey: ['home', 'feature-requests'] as const,
  queryFn: () => getHomeFeatureRequests(),
});

export const homeUpcomingClassesQuery = (): PlainDetailQuery => ({
  queryKey: ['home', 'upcoming-classes'] as const,
  queryFn: () => getHomeUpcomingClasses(),
});

/**
 * The /discovery loader's DEFAULT-path list (classes tab, no favourites, no
 * studio filter, empty search) — the state a bare /discovery tap always lands
 * on. Key built with the shared `discoveryClassesKey` builder (same import the
 * loader uses) so it can never drift, and the SGT "today" key is computed at
 * CALL time so a session open across SGT midnight prefetches the entry the next
 * navigation will actually build.
 */
export const discoveryClassesRootQuery = (): InfiniteDetailQuery => {
  const dateStr = toSGTCivilKey(new Date());
  return {
    queryKey: discoveryClassesKey(dateStr, dateStr, [], false, ''),
    queryFn: ({ pageParam }: { pageParam: unknown }) =>
      getClasses(dateStr, dateStr, {
        levels: [],
        genres: [],
        studios: [],
        cursor: pageParam as string | undefined,
        search: undefined,
        favourites: false,
      }),
    infinite: true,
    initialPageParam: undefined as string | undefined,
  };
};
